export function convertToCurrency(
  value?: string | number,
  precision = 0,
  currency = "INR"
) {
  return Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(Number(value || 0));
}

export function convertToPercentage(value: string | number) {
  return Intl.NumberFormat("en-IN", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export function formatAsCrore(
  value: string | number,
  precision = 2,
  stripPrefix = false
) {
  const numValue = Number(value || 0);
  const units = [
    { value: 1_00_00_000, symbol: "Cr" },
    { value: 1_00_000, symbol: "L" },
    { value: 1_000, symbol: "K" },
  ];
  // Choose unit based on absolute magnitude so negative values
  // are still formatted with Cr/L/K suffixes.
  const unit = units.find((u) => Math.abs(numValue) >= u.value);
  if (unit) {
    // Truncate (rather than round) to `precision` decimals so a value like
    // 99995 -> 99.995K doesn't round up to 100.00K, which would read as
    // belonging to the next unit up.
    const factor = 10 ** precision;
    const quotient = numValue / unit.value;
    const truncated = Math.trunc(quotient * factor) / factor;
    const result =
      convertToCurrency(truncated.toString(), precision) + unit.symbol;
    return stripPrefix ? result.replace("₹", "") : result;
  }
  const result = convertToCurrency(value || 0, precision);
  return stripPrefix ? result.replace("₹", "") : result;
}

export function formatCompact(
  value: string | number,
  precision = 2,
  stripPrefix = false
) {
  const units = [
    { value: 1_00_00_000, symbol: "Cr" },
    { value: 1_00_000, symbol: "L" },
    { value: 1_000, symbol: "K" },
  ];

  function shorten(num: number, availableUnits: typeof units): string {
    const unit = availableUnits.find((u) => num >= u.value);
    if (!unit) {
      return convertToCurrency(num, precision);
    }
    const quotient = num / unit.value;
    const remaining = availableUnits.filter((u) => u.value < unit.value);
    const inner = shorten(quotient, remaining);
    // inner already has ₹ prefix from convertToCurrency or a recursive call
    return `${inner}${unit.symbol} `;
  }

  const numValue = Number(value || 0);
  const formatted = shorten(numValue, units);
  return stripPrefix ? formatted.replace("₹", "") : formatted;
}

export function formatAsPercentage(
  value?: string | number | null,
  defaultValue: string = "N/A",
  precision = 2
) {
  if (value === null || value === undefined) return defaultValue;
  return `${Number(value).toFixed(precision)}%`;
}

export function getDecimalPlaces(num: number) {
  const str = num.toString();
  const decimalIndex = str.indexOf(".");
  return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1;
}

export function parseAsCurrency(value: string | number) {
  const currencyStr = value.toString().match(/^-?\d+(?:\.\d{0,2})?/)?.[0];
  return Number(currencyStr);
}

export type SupportedCurrency = "INR" | "USD";

const CURRENCY_UNITS: Record<
  SupportedCurrency,
  { value: number; symbol: string }[]
> = {
  INR: [
    { value: 1_00_00_000, symbol: "Cr" },
    { value: 1_00_000, symbol: "L" },
    { value: 1_000, symbol: "K" },
  ],
  USD: [
    { value: 1_000_000_000_000, symbol: "T" },
    { value: 1_000_000_000, symbol: "B" },
    { value: 1_000_000, symbol: "M" },
    { value: 1_000, symbol: "K" },
  ],
};

const CURRENCY_LOCALES: Record<SupportedCurrency, string> = {
  INR: "en-IN",
  USD: "en-US",
};

export function formatCurrency(
  value?: string | number,
  currency: SupportedCurrency = "INR",
  options: { precision?: number; trim?: boolean } = {
    precision: 2,
    trim: true,
  }
): string {
  if (value === undefined || value === null || value === "") return "-";

  const numValue = Number(value);
  if (Number.isNaN(numValue)) return "-";

  const format = (num: number) =>
    Intl.NumberFormat(CURRENCY_LOCALES[currency], {
      style: "currency",
      currency,
      minimumFractionDigits: options.precision,
      maximumFractionDigits: options.precision,
    }).format(num);

  if (options.trim) {
    const unit = CURRENCY_UNITS[currency].find(
      (u) => Math.abs(numValue) >= u.value
    );
    if (unit) {
      return `${format(numValue / unit.value)}${unit.symbol}`;
    }
  }
  return format(numValue);
}

export function formatShort(value: string | number): string {
  return formatAsCrore(value).replace(/\.?0+(?=[A-Za-z]|$)/, "");
}
